/**
 * Custom Feature Cards
 *
 * Responsibilities:
 *   1. Listen to `custom-pdp:product-change` and toggle the active group of
 *      cards so the section follows the hero carousel.
 *   2. Apply the active product's accent color (from the same event detail)
 *      to this section's CSS variables so the tube light glow matches the
 *      product.
 *   3. Dispatch `custom-pdp:detail-change` whenever the user hovers / focuses
 *      / taps a card, so the OLED detail section can swap its content to
 *      match the hovered card. Card 1 is active by default on page load.
 *   4. MOBILE (<=600px) PILL STRIP: cards render as a text-only sliding pill
 *      strip (styled in CSS). This script injects prev/next arrow buttons
 *      into every card, centers the active pill in the viewport via
 *      translateX on the group, and steps prev/next with wrap-around
 *      (1→2→3→1). Replaces the old "solo mode". Desktop/tablet behavior is
 *      completely unchanged.
 *
 * Color sync: on init, this script also reads --ch-accent / --ch-accent-rgb
 * from the hero section (if present on the page) and applies them
 * immediately. This protects against a race where the hero finishes its
 * async image color-extraction BEFORE this script attaches its event
 * listener — in that case the broadcast event would be missed.
 */
(function () {
  'use strict';

  var PRODUCT_EVENT = 'custom-pdp:product-change';
  var DETAIL_EVENT = 'custom-pdp:detail-change';
  var PILL_MQ = window.matchMedia('(max-width: 600px)');
  var MOBILE_MQ = window.matchMedia('(max-width: 1024px)');

  function setActiveGroup(root, handle) {
    if (!handle) return false;
    var groups = root.querySelectorAll('[data-product-handle]');
    var match = null;
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].getAttribute('data-product-handle') === handle) {
        match = groups[i];
        break;
      }
    }
    if (!match) return false;
    for (var j = 0; j < groups.length; j++) {
      var g = groups[j];
      if (g === match) {
        g.classList.add('is-active');
        g.removeAttribute('aria-hidden');
      } else {
        g.classList.remove('is-active');
        g.setAttribute('aria-hidden', 'true');
      }
    }
    return true;
  }

  function applyAccent(root, accentColor, accentRgb) {
    if (accentColor) root.style.setProperty('--cf-accent', accentColor);
    if (accentRgb) root.style.setProperty('--cf-accent-rgb', accentRgb);
  }

  /**
   * On init, mirror the hero's current accent into this section so the
   * colors are correct from the start, regardless of event timing.
   * The hero section sets --ch-accent / --ch-accent-rgb on its own root
   * inside dispatchProductChange(), so we read directly from there.
   */
  function syncAccentFromHero(root) {
    var hero = document.querySelector('[data-custom-hero]');
    if (!hero) return;
    var heroStyle = getComputedStyle(hero);
    var color = heroStyle.getPropertyValue('--ch-accent').trim();
    var rgb = heroStyle.getPropertyValue('--ch-accent-rgb').trim();
    applyAccent(root, color || null, rgb || null);
  }

  function setActiveCardEverywhere(root, idx) {
    var cards = root.querySelectorAll('[data-feature-card-trigger]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var thisIdx = parseInt(card.getAttribute('data-detail-index'), 10);
      if (thisIdx === idx) {
        card.classList.add('is-detail-active');
      } else {
        card.classList.remove('is-detail-active');
      }
    }
  }

  function fireDetail(idx) {
    document.dispatchEvent(new CustomEvent(DETAIL_EVENT, {
      detail: { detailIndex: idx },
      bubbles: true
    }));
  }

  /* ------------------------------------------------------------------ *
   *  Mobile pill strip (<=600px)
   * ------------------------------------------------------------------ */

  function getActiveGroupEl(root) {
    return root.querySelector('.custom-feature-cards__group.is-active');
  }

  function getViewportEl(root) {
    return root.querySelector('.custom-feature-cards__inner');
  }

  /**
   * Center the given card's pill in the viewport by translating the
   * whole group (same math as the Brand Showcase carousel).
   */
  function centerCard(root, card) {
    var group = getActiveGroupEl(root);
    var viewport = getViewportEl(root);
    if (!group || !viewport || !card) return;
    if (!PILL_MQ.matches) {
      group.style.transform = '';
      return;
    }
    var x = (viewport.offsetWidth / 2) - (card.offsetLeft + card.offsetWidth / 2);
    group.style.transform = 'translateX(' + x + 'px)';
  }

  /**
   * Re-center on whatever is currently active (or clear the inline
   * transform entirely when the viewport is above the pill breakpoint).
   */
  function recenter(root) {
    var group = getActiveGroupEl(root);
    if (!group) return;
    if (!PILL_MQ.matches) {
      var groups = root.querySelectorAll('.custom-feature-cards__group');
      for (var i = 0; i < groups.length; i++) {
        groups[i].style.transform = '';
      }
      return;
    }
    var active = group.querySelector('.is-detail-active') ||
                 group.querySelector('[data-feature-card-trigger]');
    centerCard(root, active);
  }

  function currentIndex(root) {
    var group = getActiveGroupEl(root);
    var active = group && group.querySelector('.is-detail-active');
    return active ? parseInt(active.getAttribute('data-detail-index'), 10) : 1;
  }

  /**
   * Activate slot idx: toggle classes across ALL groups (so a later
   * product switch lands on the same slot), fire the detail event for the
   * OLED section, and slide the active group's pill to center.
   */
  function selectIndex(root, idx) {
    setActiveCardEverywhere(root, idx);
    fireDetail(idx);
    var group = getActiveGroupEl(root);
    var target = group && group.querySelector(
      '[data-feature-card-trigger][data-detail-index="' + idx + '"]'
    );
    centerCard(root, target);
  }

  /** Step prev/next with wrap-around 1→2→3→1. */
  function stepIndex(root, direction) {
    var idx = currentIndex(root) + direction;
    if (idx < 1) idx = 3;
    if (idx > 3) idx = 1;
    selectIndex(root, idx);
  }

  /** Inject prev/next arrow buttons into every card (hidden above 600px by CSS). */
  function injectArrows(root) {
    var cards = root.querySelectorAll('[data-feature-card-trigger]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.querySelector('.cf-pill-arrow')) continue; // already injected

      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'cf-pill-arrow cf-pill-arrow--prev';
      prev.setAttribute('data-cf-arrow', 'prev');
      prev.setAttribute('aria-label', 'Previous feature');

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'cf-pill-arrow cf-pill-arrow--next';
      next.setAttribute('data-cf-arrow', 'next');
      next.setAttribute('aria-label', 'Next feature');

      card.appendChild(prev);
      card.appendChild(next);
    }
  }

  function bindCardInteractions(root) {
    var cards = root.querySelectorAll('[data-feature-card-trigger]');
    for (var i = 0; i < cards.length; i++) {
      (function (card) {
        var idx = parseInt(card.getAttribute('data-detail-index'), 10);
        if (!idx) return;

        function activate() {
          setActiveCardEverywhere(root, idx);
          fireDetail(idx);
        }

        card.addEventListener('mouseenter', function () {
          // On mobile, the emulated mouseenter from a tap mutates the DOM,
          // which makes iOS/Android swallow the click (forcing a second
          // tap). Hover activation is desktop-only.
          if (MOBILE_MQ.matches) return;
          activate();
        });
        card.addEventListener('focus', function () {
          activate();
          if (PILL_MQ.matches) recenter(root);
        });
        card.addEventListener('click', function (e) {
          // Arrow taps are handled by the delegated capture listener; a
          // click on an arrow still bubbles to the card, so skip it here.
          if (e.target.closest && e.target.closest('.cf-pill-arrow')) return;

          if (PILL_MQ.matches) {
            selectIndex(root, idx);
          } else {
            activate();
          }
          e.preventDefault();
        });
      })(cards[i]);
    }
  }

  /** Arrow taps — delegated so injected buttons need no per-node binding. */
  function bindArrowInteractions(root) {
    root.addEventListener('click', function (e) {
      if (!PILL_MQ.matches) return;
      var arrow = e.target.closest && e.target.closest('[data-cf-arrow]');
      if (!arrow || !root.contains(arrow)) return;
      e.stopPropagation();
      e.preventDefault();
      stepIndex(root, arrow.getAttribute('data-cf-arrow') === 'prev' ? -1 : 1);
    }, true);
  }

  function init() {
    var roots = document.querySelectorAll('[data-feature-cards]');
    for (var i = 0; i < roots.length; i++) {
      var el = roots[i];
      if (el.__customFeatureCardsInited) continue;
      el.__customFeatureCardsInited = true;

      injectArrows(el);
      bindCardInteractions(el);
      bindArrowInteractions(el);

      // Immediately mirror the hero's current accent.
      syncAccentFromHero(el);

      // Re-check on the next frame in case the hero is still extracting
      // colors asynchronously (image-based dominant color extraction).
      requestAnimationFrame(function () { syncAccentFromHero(el); });
      // And once more after the typical image-load window, as a safety net.
      setTimeout(function () { syncAccentFromHero(el); }, 800);

      (function (root) {
        document.addEventListener(PRODUCT_EVENT, function (e) {
          if (!e.detail) return;
          if (e.detail.handle) {
            setActiveGroup(root, e.detail.handle);
            // New group: snap the strip to its active pill on mobile.
            if (PILL_MQ.matches) {
              requestAnimationFrame(function () { recenter(root); });
            }
          }
          applyAccent(root, e.detail.accentColor, e.detail.accentRgb);
        });

        // Keep the strip centered across viewport resizes, and clear the
        // inline transform when leaving mobile so desktop is untouched.
        window.addEventListener('resize', function () { recenter(root); });
        PILL_MQ.addEventListener('change', function () { recenter(root); });

        // Initial centering (twice — second pass catches late layout
        // shifts from web fonts, same pattern as the Brand Showcase).
        requestAnimationFrame(function () { recenter(root); });
        setTimeout(function () { recenter(root); }, 400);
      })(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();